<script lang="ts">
import { computed, defineComponent, inject, PropType, ref, watch } from "vue";
import Steps from "@/components/Steps.vue";
import formValidierung from "@/businesslogic/formValidierung";
import { NavvItem } from "@/model/NavItem";
import TarifauswahlInformationenHG from "@/components/tarif/TarifauswahlInformationenHG";
import LeistungenHGDirekt from "@/components/leistungstabelle/LeistungenHGDirekt";
import LeistungenHGMakler from "@/components/leistungstabelle/LeistungenHGMakler";
import ValidierungsFehler from "@/components/ValidierungsFehler.vue";
import EmailOderHerunterladen from "@/components/vorschlag/EmailOderHerunterladen.vue";
import Maklerrabatt from "@/components/maklerrabatt/Maklerrabatt.vue";
import Beitrag from "@/components/Beitrag.vue";
import Infotext from "@/components/Infotext.vue";
import IconSprite from "@/components/IconSprite.vue";
import LeistungstabelleZweiLinien from "@/components/leistungstabelle/LeistungstabelleZweiLinien.vue";
import TarifoptionInformationenHG from "@/components/tarifoptionen/TarifoptionInformationenHG";
import Tarifoptionen from "@/components/tarifoptionen/Tarifoptionen.vue";
import Tarifauswahl from "@/components/tarif/Tarifauswahl.vue";
import Logger from "js-logger";
import { useFormStore } from "@/businesslogic/stores/formStore";

export default defineComponent({
  name: "TarifHG",
  emits: ["prev-page", "next-page", "goto-page", "handle-error"],
  components: {
    Tarifauswahl,
    LeistungstabelleZweiLinien,
    Tarifoptionen,
    Infotext,
    Steps,
    ValidierungsFehler,
    Maklerrabatt,
    IconSprite,
    Beitrag,
    EmailOderHerunterladen,
  },
  props: {
    navItems: Object as PropType<NavvItem[]>,
    anfrageId: String,
  },
  setup(props, { emit }) {
    const piniaStore = useFormStore();
    const istSichtbar: any = inject("istSichtbar");
    const wechsleSichtbarkeit: any = inject("wechsleSichtbarkeit");

    const tarifauswahlInformationen = ref(TarifauswahlInformationenHG);
    const tarifoptionInformationen = ref(TarifoptionInformationenHG);
    const aktiveLinie = ref(2);
    const { validiereForm, getFormFehlerAnzahl, getFormFehler, validiereRabatt } = formValidierung();
    let validiert = false;

    const fehler = ref({} as any);

    const tib = computed(() => {
      if (piniaStore.state.isMakler) {
        return "https://www.gev-versicherung.de/downloads/pdf/TI-TIB-HG-Protect-2021.pdf";
      }
      return "https://www.gev-versicherung.de/downloads/pdf/TI-TIB-HG-HomeCare-2021.pdf";
    });

    const eingabenSindValide = () => {
      return new Promise((resolve) => {
        validiereForm("").then(() => {
          fehler.value = getFormFehler();
          validiert = true;
          validiereRabatt(piniaStore.state, fehler, validiert);
          //Spezialprüfung 50 Mio-Deckung nur bei Max/Premium
          if (
            piniaStore.state.tarifinformationen.produktLinie == "002" &&
            piniaStore.state.tarifinformationen.deckungssumme === 500
          ) {
            fehler.value["deckungssumme"] =
              "Die Deckungssumme in Höhe von 50 Mio. ist im " +
              piniaStore.state.preise.produktLinien[0].nameProduktLinie +
              "-Tarif nicht versicherbar. Bitte wählen Sie eine andere Deckungssumme oder den " +
              piniaStore.state.preise.produktLinien[1].nameProduktLinie +
              "-Tarif aus.";
          }
          resolve(getFormFehlerAnzahl() === 0);
        });
      });
    };

    const nextPage = () => {
      eingabenSindValide().then((isValid) => {
        if (isValid) {
          emit("next-page", { pageIndex: 1 });
        }
      });
    };

    const wechsleTarif = () => {
      if (piniaStore.state.tarifinformationen.produktLinie === "002") {
        //Deckungssumme von 50 auf 25 runtersetzen
        if (piniaStore.state.tarifinformationen.deckungssumme === 500) {
          Logger.debug("wechsleTarif(): Passe Deckungssumme von 50 auf 25 Mio an.");
          piniaStore.state.tarifinformationen.deckungssumme = 250;
        }
        aktiveLinie.value = 1;
      } else if (piniaStore.state.tarifinformationen.produktLinie === "003") {
        aktiveLinie.value = 2;
      } else {
        Logger.debug("wechsleTarif(): Unbekannte Produktlinie:" + piniaStore.state.tarifinformationen.produktLinie);
      }
    };

    const istGewaehlteProduktLinie = (produktLinie: string) => {
      return piniaStore.state.tarifinformationen.produktLinie === produktLinie;
    };

    const leistungen = computed(() => {
      if (piniaStore.state.isMakler) {
        return LeistungenHGMakler;
      }
      return LeistungenHGDirekt;
    });

    watch(
      () => piniaStore.state.tarifinformationen.produktLinie,
      (tarifNeu, tarifAlt) => {
        if (tarifNeu !== tarifAlt) {
          wechsleTarif();
        }
      },
    );

    watch(
      () => piniaStore.state.tarifinformationen.abschlagZuschlagHoehe,
      () => {
        validiert = true;
        validiereRabatt(piniaStore.state, fehler, validiert);
      },
    );

    return {
      piniaStore,
      nextPage,
      fehler,
      tarifauswahlInformationen,
      leistungen,
      tarifoptionInformationen,
      wechsleSichtbarkeit,
      tib,
      istSichtbar,
      aktiveLinie,
      istGewaehlteProduktLinie,
    };
  },
});
</script>

<template>
  <steps v-bind:items="navItems" :active="1" @goto-page="$emit('goto-page', $event)" />
  <section class="js-step-2">
    <tarifauswahl :tarifauswahl-informationen="tarifauswahlInformationen" />
    <leistungstabelle-zwei-linien :inhalt="leistungen" :aktive-linie="aktiveLinie" :link="tib" />
    <tarifoptionen tarifoptionentitel="Deckungssumme" :tarifoptionInformationen="tarifoptionInformationen" />
    <div v-if="fehler.deckungssumme" class="o-grid">
      <div
        class="o-grid__cell o-grid__cell--12 o-grid__cell--9-480 o-grid__cell--8-768 o-grid__cell--7-1024 o-grid__cell--6-1200 o-grid__cell--offset-3-480"
      >
        <div class="o-grid o-grid--no-y-gutter o-grid--align-flex-end">
          <div class="o-grid__cell o-grid__cell--12">
            <validierungs-fehler :fehler-text="fehler.deckungssumme" />
          </div>
        </div>
      </div>
    </div>
    <div class="o-grid u-mar-t-2">
      <div class="o-grid__cell o-grid__cell--12 o-grid__cell--3-480 u-text-align-right--480 u-form-grid-nospace-y">
        <h2 class="u-form-heading unset-text-align">Rabatte</h2>
      </div>
      <div
        class="o-grid__cell o-grid__cell--12 o-grid__cell--9-480 o-grid__cell--8-768 o-grid__cell--7-1024 o-grid__cell--6-1200 u-form-grid-nospace-y"
      >
        <div class="c-form-control-checkbox gev19__contexthelp">
          <input
            v-model="piniaStore.state.tarifinformationen.buendelRabatt"
            id="buendelRabatt"
            name="buendelRabatt"
            class="c-form-control-checkbox__input"
            type="checkbox"
            :value="true"
            aria-label="Ja, ich habe bereits eine Versicherung bei der GEV"
            data-cy="buendelRabatt"
          />
          <label class="c-form-control-checkbox__label" for="buendelRabatt" style="max-width: 92%">
            <icon-sprite icon-name="icon-check-mark" css-klassen="c-form-control-checkbox__icon" :ist-klein="true" />
            <span class="c-form-control-checkbox__label-text">
              {{ "Ja, ich habe bereits eine Versicherung bei der GEV " }}<strong>(10% Rabatt)</strong>
            </span>
          </label>
          <span
            class="gev19__contexthelp-open"
            title="Hilfe anzeigen"
            @click="wechsleSichtbarkeit('buendelRabattInfo')"
          />
          <infotext
            name="buendelRabattInfo"
            text="Wir gewähren Ihnen bei Policierung des Antrages 10% Rabatt, wenn Sie bereits eine Versicherung bei uns abgeschlossen haben."
          />
        </div>

        <div class="c-form-control-checkbox gev19__contexthelp">
          <input
            v-model="piniaStore.state.tarifinformationen.hugMitglied"
            id="tariffDiscountHuG"
            name="tariffDiscountHuG"
            class="c-form-control-checkbox__input"
            type="checkbox"
            :value="true"
            aria-label="Ja, ich bin Haus und Grund Mitglied"
            data-cy="hugMitglied"
          />
          <label class="c-form-control-checkbox__label" for="tariffDiscountHuG" style="max-width: 92%">
            <icon-sprite icon-name="icon-check-mark" css-klassen="c-form-control-checkbox__icon" :ist-klein="true" />
            <span class="c-form-control-checkbox__label-text">
              Ja, ich bin Haus &amp; Grund-Mitglied
              <strong>(10% Rabatt)</strong>
            </span>
          </label>
          <span
            class="gev19__contexthelp-open"
            title="Hilfe anzeigen"
            @click="wechsleSichtbarkeit('tariffDiscountHuG')"
          />
          <infotext
            name="tariffDiscountHuG"
            text="Wir gewähren Ihnen bei Policierung des Antrages 10% Rabatt, wenn Sie Mitglied in einem Haus & Grund-Verein sind."
          />
        </div>

        <div
          v-if="
            piniaStore.state.gebaeudedaten.unbebautesGruendstueck === true ||
            (piniaStore.state.gebaeudedaten.unbebautesGruendstueck === false &&
              piniaStore.state.gebaeudedaten.gebaeudetyp !== '0003')
          "
          class="c-form-control-checkbox gev19__contexthelp"
        >
          <input
            v-model="piniaStore.state.tarifinformationen.winterdienstRabatt"
            id="winterdienstRabatt"
            name="winterdienstRabatt"
            class="c-form-control-checkbox__input"
            type="checkbox"
            :value="true"
            aria-label="Ja, der Winterdienst wird durch eine Firma durchgeführt"
            data-cy="winterdienstRabatt"
          />
          <label class="c-form-control-checkbox__label" for="winterdienstRabatt">
            <icon-sprite icon-name="icon-check-mark" css-klassen="c-form-control-checkbox__icon" :ist-klein="true" />
            <span class="c-form-control-checkbox__label-text">
              Ja, der Winterdienst wird durch eine Firma durchgeführt
              <strong>(5% Rabatt)</strong>
            </span>
          </label>
          <span
            class="gev19__contexthelp-open"
            title="Hilfe anzeigen"
            @click="wechsleSichtbarkeit('winterdienstRabatt')"
          />
          <infotext
            name="winterdienstRabatt"
            text="Wir gewähren Ihnen bei Policierung des Antrages 5% Rabatt, wenn Sie den Winterdienst von einer Firma durchführen lassen."
          />
        </div>
      </div>
    </div>
    <maklerrabatt :fehler="fehler" />
    <beitrag @next-page="nextPage" />
    <email-oder-herunterladen produkt="bh" tracking-zusatz="tarif" />
  </section>
</template>
